import { LoadingButton } from '@mui/lab';
import { Box, Button, DialogTitle, Grid, IconButton, Tooltip } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import JM from 'jalali-moment';
import { MuiColorInput } from 'mui-color-input';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
import { GeneralDatePicker, GeneralSelectMenu, GeneralTimePicker, GeneralUpload } from 'src/components/general';
import GeneralMediaUploader from 'src/components/general/GeneralMediaUploader';
import { FormProvider, RHFSwitch, RHFTextField } from 'src/components/hook-form';
import { NOTIFICATION_POPUP_LIST } from 'src/constants/queryKeys';
import {
  networkCreateNotificationPopup,
  networkUpdateNotificationPopup,
  useFetchNotificationPopupCloseModeList,
  useFetchNotificationPopupLinkTypeList,
  useFetchNotificationPopupTypeList,
  useFetchNotificationPopupValueTypeList,
} from 'src/network/notification-popup-list';
import useFormsModalStore from 'src/store/forms';
import { generateSelectMenuItems } from 'src/utils';
import { getDefaultValues } from './defaultValues';
import { DialogAnimate } from 'src/components/animate';
import NotificationStyle from 'src/pages/dashboard/NotificationStyleList';
import PopupTypeGenerator from './PopupTypeGenerator';
import { getSchema } from './validationSchema';
import { yupResolver } from '@hookform/resolvers/yup';
import { SkeletonForm } from 'src/components/skeleton';
import { useFetchNotificationStyleList } from 'src/network/notification-style-list';

const notificationPopupShowOnlyForTypes = [
  { id: 1, title: 'نمایش عمومی برای همه' },
  { id: 2, title: 'کاربران' },
  { id: 3, title: 'کاربران مهمان' },
];

const notificationPopupTypeUrlPrefixTypes = [
  { id: 1, title: 'مساوی با url' },
  { id: 2, title: 'شامل بخشی از url' },
];

const notificationPopupHasButtonTypes = [
  { id: 1, title: 'بله' },
  { id: 2, title: 'خیر' },
];

const notificationStyleTypes = [
  { id: 1, title: 'مربعی' },
  { id: 2, title: 'مستطیل افقی' },
  { id: 3, title: 'مستطیل عمودی' },
];

const NotificationPopupForm = () => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { modal, closeModal } = useFormsModalStore();

  const [isLoading, setIsLoading] = useState(true);

  const notificationPopupData = modal?.data;
  const defaultValues = getDefaultValues(notificationPopupData);
  const isEditMode = notificationPopupData;

  const startTimeArray = notificationPopupData?.NotificationPopupStartTime?.split(':');
  const endTimeArray = notificationPopupData?.NotificationPopupEndTime?.split(':');

  // ---------------------------------------------------------------

  const [isOpen, setIsOpen] = useState(false);
  const [selectedPopupFrame, setSelectedPopupFrame] = useState();

  const [startDate, setStartDate] = useState(notificationPopupData ? new Date(defaultValues?.NotificationPopupStartDate) : new Date());
  const [endDate, setEndDate] = useState(notificationPopupData ? new Date(defaultValues?.NotificationPopupEndDate) : new Date());

  const [startTime, setStartTime] = useState(
    startTimeArray?.length === 3
      ? new Date().setHours(startTimeArray?.[0], startTimeArray?.[1], startTimeArray?.[2])
      : new Date().setHours(0, 0, 0, 0)
  );

  const [endTime, setEndTime] = useState(
    endTimeArray?.length === 3
      ? new Date().setHours(endTimeArray?.[0], endTimeArray?.[1], endTimeArray?.[2])
      : new Date().setHours(23, 59, 59, 999)
  );

  const [manipulatedStartTime, setManipulatedStartTime] = useState(
    defaultValues?.NotificationPopupStartTime || JM(new Date().setHours(0, 0, 0, 0)).format('HH:mm:ss')
  );
  const [manipulatedEndTime, setManipulatedEndTime] = useState(
    defaultValues?.NotificationPopupEndTime || JM(new Date().setHours(23, 59, 59, 999)).format('HH:mm:ss')
  );

  // ---------------------------------------------------------------

  const [popupTypeList, setPopupTypeList] = useState([]);
  const [selectedPopupType, setSelectedPopupType] = useState('');

  const [popupLinkTypeList, setPopupLinkTypeList] = useState([]);
  const [selectedPopupLinkType, setSelectedPopupLinkType] = useState('');

  const [popupValueTypeList, setPopupValueTypeList] = useState([]);
  const [selectedPopupValueType, setSelectedPopupValueType] = useState('');

  const [popupCloseModeList, setPopupCloseModeList] = useState([]);
  const [selectedPopupCloseMode, setSelectedPopupCloseMode] = useState('');

  const [selectedNotificationPopupShowOnlyFor, setSelectedNotificationPopupShowOnlyFor] = useState('');

  const [selectedNotificationPopupTypeUrlPrefix, setSelectedNotificationPopupTypeUrlPrefix] = useState('');

  const [selectedNotificationPopupHasButton, setSelectedNotificationPopupHasButton] = useState();

  // ---------------------------------------------------------------

  const [popupButtonBgColor, setPopupButtonBgColor] = useState(notificationPopupData?.NotificationPopupColorButton || 'transparent');
  const [popupButtonTextColor, setPopupButtonTextColor] = useState(
    notificationPopupData?.NotificationPopupTextColorButton || 'transparent'
  );

  const [isOpenPopuoPreviewModal, setIsOpenPopuoPreviewModal] = useState(false);

  // ---------------------------------------------------------------

  const methods = useForm({
    resolver: yupResolver(getSchema(selectedPopupType, selectedNotificationPopupHasButton === 1 ? true : false)),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
    setValue,
    trigger,
  } = methods;

  // ---------------------------------------------------------------

  const { data: popupTypes } = useFetchNotificationPopupTypeList();
  const { data: popupLinkTypes } = useFetchNotificationPopupLinkTypeList();
  const { data: popupValueTypes } = useFetchNotificationPopupValueTypeList();
  const { data: popupCloseModes } = useFetchNotificationPopupCloseModeList();

  const { data: notificationStyleList } = useFetchNotificationStyleList();

  useEffect(() => {
    if (popupTypes && popupLinkTypes && popupValueTypes && popupCloseModes && notificationStyleList) {
      setIsLoading(false);

      // -------------------------------------------------------------------------

      const DEFAULT_NOTIFICATION_STYLE = {
        NotificationStyleID: -1,
        NotificationStyleImageUrl: '',
        NotificationStyleLable: 'بدون فریم',
        NotificationStyleType: -1,
        RowNum: 0,
      };

      if (isEditMode) {
        const notificationStyleListData = [DEFAULT_NOTIFICATION_STYLE, ...notificationStyleList?.data?.items];

        const popupFrame = notificationStyleListData?.find(
          ({ NotificationStyleID }) => NotificationStyleID === notificationPopupData?.NotificationPopupStyleID
        );

        setSelectedPopupFrame(popupFrame);

        setValue('PopupFrame', popupFrame?.NotificationStyleID === -1 ? 'بدون فریم' : popupFrame?.NotificationStyleLable);
      } else {
        setSelectedPopupFrame(DEFAULT_NOTIFICATION_STYLE);

        setValue('PopupFrame', 'بدون فریم');
      }

      // -------------------------------------------------------------------------

      const popupTypesResult = generateSelectMenuItems(popupTypes?.data?.items, 'NotificationPopupTypeLabel', 'NotificationPopupTypeID');

      const popupLinkTypesResult = generateSelectMenuItems(
        popupLinkTypes?.data?.items,
        'NotificationBannerLinkTypeLable',
        'NotificationBannerLinkTypeID'
      );

      const popupValueTypesResult = generateSelectMenuItems(
        popupValueTypes?.data?.items,
        'NotificationBannerValueTypeLable',
        'NotificationBannerValueTypeID'
      );

      const popupCloseModesResult = generateSelectMenuItems(
        popupCloseModes?.data?.items,
        'NotificationBannerCloseModeLable',
        'NotificationBannerCloseModeID'
      );

      setPopupTypeList(popupTypesResult);
      setSelectedPopupType(notificationPopupData?.NotificationPopupTypeID || popupTypesResult[0]?.id);

      setPopupLinkTypeList(popupLinkTypesResult);
      setSelectedPopupLinkType(notificationPopupData?.NotificationPopupLinkTypeID || popupLinkTypesResult[0]?.id);

      setPopupValueTypeList(popupValueTypesResult);
      setSelectedPopupValueType(notificationPopupData?.NotificationPopupValueTypeID || popupValueTypesResult[0]?.id);

      setPopupCloseModeList(popupCloseModesResult);
      setSelectedPopupCloseMode(notificationPopupData?.NotificationPopupCloseModeID || popupCloseModesResult[0]?.id);

      setSelectedNotificationPopupShowOnlyFor(
        notificationPopupData?.NotificationPopupShowOnlyFor || notificationPopupShowOnlyForTypes[0]?.id
      );

      setSelectedNotificationPopupTypeUrlPrefix(
        notificationPopupData?.NotificationPopupTypeUrlPrefix || notificationPopupTypeUrlPrefixTypes[0]?.id
      );

      setSelectedNotificationPopupHasButton(isEditMode ? (notificationPopupData?.NotificationPopupHasButton === 1 ? 1 : 2) : 1);
    }
  }, [popupTypes, popupLinkTypes, popupValueTypes, popupCloseModes, notificationStyleList]);

  // ---------------------------------------------------------------

  const onSubmit = async (values, event) => {
    const { PopupFrame, ...restValues } = values;

    const PAYLOAD = {
      ...restValues,
      NotificationPopupApplicationID: +projectId,
      NotificationPopupTypeID: selectedPopupType,
      NotificationPopupLinkTypeID: selectedPopupLinkType,
      NotificationPopupValueTypeID: selectedPopupValueType,
      NotificationPopupCloseModeID: selectedPopupCloseMode,
      NotificationPopupShowOnlyFor: selectedNotificationPopupShowOnlyFor,
      NotificationPopupTypeUrlPrefix: selectedNotificationPopupTypeUrlPrefix,
      NotificationPopupHasButton: selectedNotificationPopupHasButton === 1 ? 1 : 0,
      NotificationPopupStyleID: selectedPopupFrame?.NotificationStyleID,
      NotificationPopupStartDate: new Date(startDate).toISOString(),
      NotificationPopupEndDate: new Date(endDate).toISOString(),
      NotificationPopupStartTime: manipulatedStartTime,
      NotificationPopupEndTime: manipulatedEndTime,
      NotificationPopupNumberPerDay: Number(values?.NotificationPopupNumberPerDay),
      NotificationPopupMinuteWithDelay: Number(values?.NotificationPopupMinuteWithDelay),
      NotificationPopupUserLevel: Number(values?.NotificationPopupUserLevel),
      NotificationPopupStatus: values?.NotificationPopupStatus ? 1 : 0,
      NotificationPopupColorButton: popupButtonBgColor,
      NotificationPopupTextColorButton: popupButtonTextColor,
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateNotificationPopup(notificationPopupData?.NotificationPopupID, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('اعلان موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
            await queryClient.invalidateQueries([NOTIFICATION_POPUP_LIST]);
          }
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      } else {
        const res = await networkCreateNotificationPopup(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('اعلان موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          reset();
          await queryClient.invalidateQueries([NOTIFICATION_POPUP_LIST]);
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const selectPopupFrame = (item) => {
    setIsOpen(false);
    setSelectedPopupFrame(item);

    const popupFrameType = notificationStyleTypes?.find(({ id }) => id === item?.NotificationStyleType)?.title;

    setValue('PopupFrame', item?.NotificationStyleID === -1 ? 'بدون فریم' : popupFrameType);
  };

  return (
    <>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ p: 3 }}>
              {isLoading ? (
                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                  }}
                >
                  <SkeletonForm number={12} />
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: 'grid',
                      columnGap: 2,
                      rowGap: 3,
                      gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                    }}
                  >
                    <RHFTextField name="NotificationPopupTitle" type="text" label="عنوان اعلان در پروژه" />
                    <RHFTextField name="NotificationPopupTitlePanel" type="text" label="عنوان اعلان در پنل" />

                    <RHFTextField name="NotificationPopupWidth" type="text" label="طول اعلان" />
                    <RHFTextField name="NotificationPopupHeight" type="text" label="عرض اعلان" />

                    <GeneralSelectMenu
                      label="نوع اعلان"
                      name="NotificationPopupTypeID"
                      options={popupTypeList}
                      value={selectedPopupType}
                      onChangeValue={setSelectedPopupType}
                      hasButton={true}
                      btnTitle="پیشنمایش"
                      onClick={() => setIsOpenPopuoPreviewModal(true)}
                    />

                    <RHFTextField
                      name="PopupFrame"
                      type="text"
                      label="نوع فریم"
                      InputProps={{
                        endAdornment: (
                          <Button variant="contained" color="inherit" onClick={() => setIsOpen(true)}>
                            انتخاب
                          </Button>
                        ),
                      }}
                    />

                    <GeneralUpload
                      label="تصویر اعلان"
                      id="notification-popup-image-url"
                      name="NotificationPopupImageUrl"
                      afterChangeAction={(res) => {
                        setValue('NotificationPopupImageUrl', res);
                        trigger('NotificationPopupImageUrl');
                      }}
                    />

                    <RHFTextField name="NotificationPopupImageLink" type="text" label="لینک تصویر اعلان" />

                    <GeneralMediaUploader
                      label="ویدیوی اعلان"
                      id="notification-popup-link-mp4"
                      name="NotificationPopupLinkMp4"
                      afterChangeAction={(res) => {
                        setValue('NotificationPopupLinkMp4', res);
                        trigger('NotificationPopupLinkMp4');
                      }}
                    />

                    <RHFTextField name="NotificationPopupUserLevel" type="text" label="سطح کاربر" />

                    <GeneralSelectMenu
                      label="نوع لینک اعلان"
                      name="NotificationPopupLinkTypeID"
                      options={popupLinkTypeList}
                      value={selectedPopupLinkType}
                      onChangeValue={setSelectedPopupLinkType}
                    />

                    <GeneralSelectMenu
                      label="نوع محتوای اعلان"
                      name="NotificationPopupValueTypeID"
                      options={popupValueTypeList}
                      value={selectedPopupValueType}
                      onChangeValue={setSelectedPopupValueType}
                    />

                    <GeneralSelectMenu
                      label="نحوه ی بستن اعلان"
                      name="NotificationPopupCloseModeID"
                      options={popupCloseModeList}
                      value={selectedPopupCloseMode}
                      onChangeValue={setSelectedPopupCloseMode}
                    />

                    <GeneralSelectMenu
                      label="نحوه ی نمایش اعلان"
                      name="NotificationPopupShowOnlyFor"
                      options={notificationPopupShowOnlyForTypes}
                      value={selectedNotificationPopupShowOnlyFor}
                      onChangeValue={setSelectedNotificationPopupShowOnlyFor}
                    />

                    {/* // todo */}
                    <RHFTextField name="NotificationPopupUrlPrefix" type="text" label="NotificationPopupUrlPrefix" />

                    <GeneralSelectMenu
                      label="NotificationPopupTypeUrlPrefix" // todo
                      name="NotificationPopupTypeUrlPrefix"
                      options={notificationPopupTypeUrlPrefixTypes}
                      value={selectedNotificationPopupTypeUrlPrefix}
                      onChangeValue={setSelectedNotificationPopupTypeUrlPrefix}
                    />

                    <GeneralSelectMenu
                      label="آیا دکمه نمایش داده شود؟"
                      name="notification-popup-has-button-types"
                      options={notificationPopupHasButtonTypes}
                      value={selectedNotificationPopupHasButton}
                      onChangeValue={setSelectedNotificationPopupHasButton}
                    />

                    {selectedNotificationPopupHasButton === 1 ? (
                      <>
                        <div />

                        <RHFTextField name="NotificationPopupTitleButton" type="text" label="عنوان دکمه" />

                        <RHFTextField name="NotificationPopupLinkButton" type="text" label="لینک دکمه" />

                        <div dir="ltr" className="w-full relative">
                          <MuiColorInput
                            format="hex"
                            label="رنگ پس زمینه دکمه"
                            value={popupButtonBgColor}
                            onChange={(color) => setPopupButtonBgColor(color)}
                            sx={{ textAlign: 'right', width: '100%' }}
                            isAlphaHidden={true}
                            placeholder="رنگ پس زمینه دکمه"
                          />

                          <span className="absolute right-6 top-4">
                            <Tooltip title="حذف رنگ پس زمینه">
                              <IconButton onClick={() => setPopupButtonBgColor('transparent')} className="absolute left-0">
                                <Iconify icon={'eva:trash-2-outline'} sx={{ fontSize: '20px', position: 'absolute', right: '0%' }} />
                              </IconButton>
                            </Tooltip>
                          </span>
                        </div>

                        <div dir="ltr" className="w-full relative">
                          <MuiColorInput
                            format="hex"
                            label="رنگ متن دکمه"
                            value={popupButtonTextColor}
                            onChange={(color) => setPopupButtonTextColor(color)}
                            sx={{ textAlign: 'right', width: '100%' }}
                            isAlphaHidden={true}
                            placeholder="رنگ متن دکمه"
                          />

                          <span className="absolute right-6 top-4">
                            <Tooltip title="حذف رنگ متن">
                              <IconButton onClick={() => setPopupButtonTextColor('transparent')} className="absolute left-0">
                                <Iconify icon={'eva:trash-2-outline'} sx={{ fontSize: '20px', position: 'absolute', right: '0%' }} />
                              </IconButton>
                            </Tooltip>
                          </span>
                        </div>
                      </>
                    ) : (
                      <RHFTextField name="NotificationPopupUserLevel" type="text" label="سطح کاربر" />
                    )}

                    <RHFTextField name="NotificationPopupNumberPerDay" type="text" label="تعداد نمایش در روز" />

                    <RHFTextField name="NotificationPopupMinuteWithDelay" type="text" label="مدت زمان تاخیر در نمایش (دقیقه)" />

                    <GeneralDatePicker
                      label="تاریخ شروع"
                      name="NotificationBannerDateStart"
                      value={startDate}
                      onChangeValue={setStartDate}
                    />

                    <GeneralDatePicker
                      minDate={startDate}
                      label="تاریخ پایان"
                      name="NotificationBannerDateExpire"
                      value={endDate}
                      onChangeValue={setEndDate}
                    />

                    <GeneralTimePicker
                      label="ساعت شروع نمایش"
                      name="NotificationBannerTimeStart"
                      value={startTime}
                      onChangeValue={setStartTime}
                      setManipulatedStartTime={setManipulatedStartTime}
                      type="start"
                    />

                    <GeneralTimePicker
                      label="ساعت پایان نمایش"
                      name="NotificationBannerTimeEnd"
                      value={endTime}
                      onChangeValue={setEndTime}
                      setManipulatedEndTime={setManipulatedEndTime}
                      type="end"
                    />
                  </Box>

                  {![5, 6]?.includes(selectedPopupType) ? (
                    <RHFTextField sx={{ mt: 3 }} name="NotificationPopupBody" multiline rows={6} label="متن اعلان" maxCharacters={1500} />
                  ) : null}

                  <RHFSwitch name="NotificationPopupStatus" labelPlacement="end" label="آیا نمایش داده شود؟" />
                </>
              )}

              <div className="flex items-center gap-4 mt-6">
                <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                  ذخیره
                </LoadingButton>
                <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                  {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
                </LoadingButton>
                <Button onClick={closeModal} variant="contained" color="error">
                  انصراف
                </Button>
              </div>
            </Box>
          </Grid>
        </Grid>
      </FormProvider>

      <DialogAnimate open={isOpen} onClose={() => setIsOpen(false)} maxWidth="md">
        <DialogTitle sx={{ textAlign: 'center', mb: 2 }}>انتخاب فریم</DialogTitle>
        <div style={{ maxHeight: '80vh' }}>
          <NotificationStyle selectPopupFrame={selectPopupFrame} />
        </div>
      </DialogAnimate>

      <DialogAnimate open={isOpenPopuoPreviewModal} onClose={() => setIsOpenPopuoPreviewModal(false)} maxWidth="sm">
        <DialogTitle sx={{ textAlign: 'center' }}>پیشنمایش اعلان</DialogTitle>
        <div className="py-10">
          <PopupTypeGenerator popupTypeId={selectedPopupType} />
        </div>
      </DialogAnimate>
    </>
  );
};

export default NotificationPopupForm;
